import React from "react";

// ─── Shimmer keyframe injected once ───────────────────────────────────────────
const SHIMMER_STYLE = `
@keyframes shimmer {
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
}
.skeleton-shimmer {
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 37%,
    #f0f0f0 63%
  );
  background-size: 1200px 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  border-radius: 8px;
}
`;

// ─── Primitive block ───────────────────────────────────────────────────────────
const Bone = ({ w = "100%", h = 14, r = 8, mb = 0, style = {} }) => (
  <div
    className="skeleton-shimmer"
    style={{ width: w, height: h, borderRadius: r, marginBottom: mb, flexShrink: 0, ...style }}
  />
);

// ─── Single active-order skeleton ─────────────────────────────────────────────
const ActiveOrderSkeleton = () => (
  <div style={{ padding: "2%" }}>
    <div
      style={{
        background: "#fff",
        borderRadius: "14px",
        padding: "5%",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header row: pickup code + amount */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <Bone w="45%" h={14} />
        <Bone w="20%" h={18} r={6} />
      </div>

      {/* Address */}
      <Bone w="75%" h={12} mb={8} />

      {/* Items count + timer */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <Bone w={60} h={12} />
        <Bone w={80} h={12} />
      </div>

      {/* Bottom row: images + button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {/* 3 product image boxes */}
        <div style={{ display: "flex", gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <Bone key={i} w={40} h={40} r={9} />
          ))}
        </div>

        {/* Pickup button */}
        <Bone w={110} h={50} r={10} />
      </div>
    </div>
  </div>
);

// ─── Single past-order skeleton ────────────────────────────────────────────────
const PastOrderSkeleton = () => (
  <div style={{ padding: "2%" }}>
    <div
      style={{
        background: "#fff",
        borderRadius: "14px",
        padding: "5%",
        boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header row: order id + amount */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <Bone w="50%" h={14} />
        <Bone w="18%" h={16} r={6} />
      </div>

      {/* Address */}
      <Bone w="80%" h={12} mb={6} />
      <Bone w="55%" h={12} mb={14} />

      {/* Items count + status pill */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <Bone w={55} h={12} />
        <Bone w={65} h={12} r={20} />
      </div>

      {/* Bottom row: images + button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 8 }}>
          {[0, 1, 2].map((i) => (
            <Bone key={i} w={40} h={40} r={9} />
          ))}
        </div>
        <Bone w={110} h={50} r={10} />
      </div>
    </div>
  </div>
);

// ─── Section heading skeleton ──────────────────────────────────────────────────
const HeadingSkeleton = () => (
  <div style={{ padding: "15px 15px 8px" }}>
    <Bone w={180} h={22} r={6} />
  </div>
);

// ─── Full page skeleton ────────────────────────────────────────────────────────
/**
 * Usage:
 *   import OrdersSkeleton from "./OrdersSkeleton";
 *   {props.loader && <OrdersSkeleton />}
 *
 * Props:
 *   activeCount  — number of active-order skeletons to show  (default 1)
 *   pastCount    — number of past-order skeletons to show    (default 4)
 */
const OrdersSkeleton = ({ activeCount = 1, pastCount = 4 }) => (
  <>
    <style>{SHIMMER_STYLE}</style>

    {/* Active orders section */}
    <HeadingSkeleton />
    {Array.from({ length: activeCount }).map((_, i) => (
      <ActiveOrderSkeleton key={`active-${i}`} />
    ))}

    {/* Past orders section */}
    <HeadingSkeleton />
    {Array.from({ length: pastCount }).map((_, i) => (
      <PastOrderSkeleton key={`past-${i}`} />
    ))}
  </>
);

export default OrdersSkeleton;

// Named exports so you can use individual pieces if needed
export { ActiveOrderSkeleton, PastOrderSkeleton, HeadingSkeleton, Bone };
